import { loginOwner } from "../features/backoffice/mock-store.ts";
import { ownerPermissions } from "../features/backoffice/permissions.ts";
import type { OwnerPrincipal, OwnerRole, Permission } from "../features/backoffice/types.ts";
import { api } from "./api.ts";
import { getAuthToken, OWNER_SESSION_TOKEN_KEY, setAuthToken } from "./auth-token.ts";
import { env } from "./env.ts";

const REFRESH_TOKEN_KEY = "jod.owner.refresh";
const PRINCIPAL_KEY = "jod.owner.principal";
const MUST_CHANGE_KEY = "jod.owner.mustChangePassword";
const ACTIVE_VENUE_KEY = "jod.owner.activeVenueId";
const VENUES_KEY = "jod.owner.venues";

export interface OwnerVenueSummary {
  id: string;
  name: string;
  status?: string;
}

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

interface OwnerApiProfile {
  id: string;
  email: string;
  displayName: string;
  roles?: string[];
  role?: string;
}

interface OwnerTokenPayload {
  accessToken: string;
  refreshToken: string;
  mustChangePassword: boolean;
  activeVenueId: string;
  owner?: OwnerApiProfile;
  venues?: OwnerVenueSummary[];
  authorizedVenueIds?: string[];
}

export class OwnerAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OwnerAuthError";
  }
}

function storage(): Storage | null {
  return typeof window === "undefined" ? null : window.sessionStorage;
}

function unwrapEnvelope<T>(result: { data?: unknown; error?: unknown }): T {
  const envelope = (result.data ?? result.error) as ApiEnvelope<T> | undefined;
  if (envelope?.success && envelope.data !== undefined && envelope.data !== null) {
    return envelope.data;
  }
  throw new OwnerAuthError(envelope?.error?.message ?? "Request failed");
}

function resolveOwnerRole(roles: string[] | undefined, fallback?: string): OwnerRole {
  const normalized = roles?.map((role) => role.toLowerCase()) ?? [];
  if (normalized.includes("owner")) return "owner";
  if (normalized.includes("manager") || normalized.includes("floor_manager")) return "manager";
  if (fallback === "manager") return "manager";
  return "owner";
}

function buildPrincipal(
  owner: OwnerApiProfile,
  activeVenueId: string,
  authorizedVenueIds: string[],
): OwnerPrincipal {
  const role = resolveOwnerRole(owner.roles, owner.role);
  return {
    id: owner.id,
    name: owner.displayName,
    email: owner.email,
    role,
    authorizedVenueIds,
    currentVenueId: activeVenueId,
    permissions: ownerPermissions[role],
  };
}

function persistSession(payload: OwnerTokenPayload): OwnerPrincipal {
  const owner = payload.owner;
  if (!owner) throw new OwnerAuthError("Owner profile missing from auth response");

  const authorizedVenueIds = payload.authorizedVenueIds ??
    payload.venues?.map((venue) => venue.id) ?? [payload.activeVenueId];

  const principal = buildPrincipal(owner, payload.activeVenueId, authorizedVenueIds);

  setAuthToken(payload.accessToken);
  storage()?.setItem(OWNER_SESSION_TOKEN_KEY, payload.accessToken);
  storage()?.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
  storage()?.setItem(PRINCIPAL_KEY, JSON.stringify(principal));
  storage()?.setItem(MUST_CHANGE_KEY, String(payload.mustChangePassword));
  storage()?.setItem(ACTIVE_VENUE_KEY, payload.activeVenueId);
  if (payload.venues) {
    storage()?.setItem(VENUES_KEY, JSON.stringify(payload.venues));
  }

  return principal;
}

function persistMockSession(principal: OwnerPrincipal, mustChangePassword = false): OwnerPrincipal {
  const accessToken = `owner-demo-${principal.id}`;
  const refreshToken = `owner-demo-refresh-${principal.id}`;
  persistSession({
    accessToken,
    refreshToken,
    mustChangePassword,
    activeVenueId: principal.currentVenueId,
    owner: {
      id: principal.id,
      email: principal.email,
      displayName: principal.name,
      roles: [principal.role],
    },
    authorizedVenueIds: principal.authorizedVenueIds,
    venues: principal.authorizedVenueIds.map((id) => ({ id, name: id })),
  });
  return principal;
}

export async function signInOwner(email: string, password: string): Promise<OwnerPrincipal> {
  if (!password.trim()) {
    throw new OwnerAuthError("Sign in failed. Check your credentials.");
  }

  if (env.apiMock) {
    const principal = loginOwner(email, password);
    if (!principal) {
      throw new OwnerAuthError("Sign in failed. Check your credentials.");
    }
    return persistMockSession(principal, email.toLowerCase() === "owner@smokemirrors.example");
  }

  const data = unwrapEnvelope<OwnerTokenPayload>(
    await api.POST("/api/v1/auth/owner/login", { body: { email, password } }),
  );
  return persistSession(data);
}

export async function changeOwnerPassword(
  currentPassword: string,
  newPassword: string,
): Promise<OwnerPrincipal> {
  if (env.apiMock) {
    const principal = getOwnerPrincipal();
    if (!principal) throw new OwnerAuthError("Session expired. Sign in again.");
    return persistMockSession(principal, false);
  }

  const data = unwrapEnvelope<OwnerTokenPayload>(
    await api.POST("/api/v1/auth/owner/change-password", {
      body: { currentPassword, newPassword },
    }),
  );
  return persistSession({ ...data, mustChangePassword: false });
}

export async function signOutOwner(): Promise<void> {
  const refreshToken = storage()?.getItem(REFRESH_TOKEN_KEY);
  if (!env.apiMock && refreshToken) {
    try {
      await api.POST("/api/v1/auth/logout", { body: { refreshToken } });
    } catch {
      // Best-effort logout; always clear local session.
    }
  }

  setAuthToken(undefined);
  storage()?.removeItem(OWNER_SESSION_TOKEN_KEY);
  storage()?.removeItem(REFRESH_TOKEN_KEY);
  storage()?.removeItem(PRINCIPAL_KEY);
  storage()?.removeItem(MUST_CHANGE_KEY);
  storage()?.removeItem(ACTIVE_VENUE_KEY);
  storage()?.removeItem(VENUES_KEY);
}

export function getOwnerPrincipal(): OwnerPrincipal | null {
  const raw = storage()?.getItem(PRINCIPAL_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OwnerPrincipal;
  } catch {
    return null;
  }
}

export function getOwnerVenues(): OwnerVenueSummary[] {
  const raw = storage()?.getItem(VENUES_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as OwnerVenueSummary[];
  } catch {
    return [];
  }
}

export function getOwnerActiveVenueId(): string | null {
  return storage()?.getItem(ACTIVE_VENUE_KEY) ?? getOwnerPrincipal()?.currentVenueId ?? null;
}

export function ownerMustChangePassword(): boolean {
  return storage()?.getItem(MUST_CHANGE_KEY) === "true";
}

export function isOwnerAuthenticated(): boolean {
  return Boolean(getAuthToken() && getOwnerPrincipal());
}

export function getOwnerPostAuthTarget(): "/change-password" | "/dashboard" {
  return ownerMustChangePassword() ? "/change-password" : "/dashboard";
}

export function getOwnerRedirectTarget(pathname: string): "/login" | "/change-password" | null {
  if (pathname === "/login" || pathname === "/change-password") return null;
  if (!isOwnerAuthenticated()) return "/login";
  if (ownerMustChangePassword()) return "/change-password";
  return null;
}

export function ownerHasPermission(permission: Permission): boolean {
  return Boolean(getOwnerPrincipal()?.permissions.includes(permission));
}
